import React, { useMemo, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Bug, CircleHelp, ExternalLink, Lightbulb, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from 'components/Button';
import { ROUTES } from 'utils/constants';
import styles from './Help.module.scss';

const FAQ_ITEMS = [
  {
    question: 'How do I start using Abrasoft Hub?',
    answer:
      'Begin from Dashboard, set your active company, then set up basics (customers, suppliers, payment methods where needed). Record Sales and Disbursements from Accounting and open Reports once you have activity.',
  },
  {
    question: 'Where can I manage my account settings?',
    answer:
      'Open Account in the sidebar, then go to App Settings, View Profile, or Change Password depending on what you need to update.',
  },
  {
    question: 'How can I view financial reports?',
    answer:
      'Open Accounting and choose your report (Income Statement, Balance Sheet, Cash Flow, and others). Adjust filters before exporting or printing.',
  },
];

const HOW_TO_STEPS = [
  {
    topic: 'organize-categories',
    text: 'Use clear categories when you record sales or expenses (add categories from those flows when needed).',
  },
  {
    topic: 'record-transactions',
    text: 'Record sales and disbursements from Accounting using Sales or Disbursements.',
  },
  {
    topic: 'review-report-outputs',
    text: 'Review report outputs in Accounting and validate totals before sharing.',
  },
  {
    topic: 'manage-account-settings',
    text: 'Use App Settings and Profile options to keep your account details updated.',
  },
] as const;

const SUPPORT_EMAIL = 'hello@abrasoft.com';

const Help: React.FC = () => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMessage, setReportMessage] = useState('');

  const mailtoHref = useMemo(() => {
    const subject = 'Abrasoft Hub Bug Report';
    const body = reportMessage.trim()
      ? reportMessage.trim()
      : 'Please describe the issue, steps to reproduce, expected result, and actual result.';
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [reportMessage]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>User Help Center</h1>
        <p>
          A quick guide for using Abrasoft Hub, including FAQs, troubleshooting,
          and how-to steps.
        </p>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>
          <CircleHelp size={20} />
          <h2>FAQ</h2>
        </div>
        <div className={styles.cardGrid}>
          {FAQ_ITEMS.map((item) => (
            <article key={item.question} className={styles.card}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>
          <Lightbulb size={20} />
          <h2>How To</h2>
        </div>
        <ol className={styles.steps}>
          {HOW_TO_STEPS.map((step) => (
            <li key={step.topic}>
              <Link
                to={ROUTES.HELP_HOW_TO.replace(':topic', step.topic)}
                className={styles.howToLink}
              >
                {step.text}
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>
          <Bug size={20} />
          <h2>Report a Bug</h2>
        </div>
        <div className={styles.reportCard}>
          <p>
            Found an issue? Send us the details so we can investigate and fix
            it. Please include steps to reproduce, expected result, and
            screenshots if available.
          </p>
          <button
            type="button"
            className={styles.reportButton}
            onClick={() => setShowReportModal(true)}
          >
            Report via Email
            <ExternalLink size={16} />
          </button>
        </div>
      </section>

      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.reportModalTitle}>
            <Mail size={18} />
            Send message to support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.reportModalRecipient}>
            <span>To:</span>
            <strong>{SUPPORT_EMAIL}</strong>
          </div>
          <Form.Group>
            <Form.Label>Your message</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              placeholder="Describe your issue, steps to reproduce, expected result, and actual result."
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setShowReportModal(false)}>
            Cancel
          </Button>
          <a href={mailtoHref} className={styles.sendEmailButton}>
            Open Email App
          </a>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Help;
